import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import { toast } from "@replo/design-system/components/toast/Toast";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useCurrentFile } from "@v2/features/page-renderer/hooks/useCurrentFile";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import {
  fileTemplateRouter,
  fileTemplateUtils,
} from "@v2/trpc/routes/fileTemplate";
import { nextJSProjectRouter } from "@v2/trpc/routes/nextJSProject";
import { writeHtmlToClipboard } from "@v2/utils/clipboard";
import { formatDistanceToNowStrict } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AffiliateShareCard } from "./AffiliateShareCard";
import { InitialCreationLoadingState } from "./InitialCreationLoadingState";
import { SharePopoverSkeleton } from "./SharePopoverSkeleton";

const templateNameSchema = z.object({
  name: z.string().min(1, "Template name is required"),
});

type TemplateNameFormData = z.infer<typeof templateNameSchema>;

export const ShareButton = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const errorToast = useErrorToast();
  const isSilentUpdateRef = React.useRef(false);

  const currentFile = useCurrentFile();
  const projectId = useCurrentProjectId();

  const { data: template, isLoading: isLoadingTemplate } =
    fileTemplateRouter.getByPageId.useQuery(
      projectId && currentFile
        ? {
            projectId,
            pageId: currentFile.id,
          }
        : skipToken,
    );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<TemplateNameFormData>({
    resolver: zodResolver(templateNameSchema),
    mode: "onBlur",
    values: {
      name: template?.name ?? currentFile?.name ?? "",
    },
  });

  const templateName = watch("name");

  const {
    mutate: createOrUpdateTemplate,
    isPending: isCreatingTemplate,
    data: templateMutationData,
  } = nextJSProjectRouter.createOrUpdateTemplateFromPage.useMutation({
    onSuccess: () => {
      setIsPopoverOpen(true);
      if (projectId && currentFile) {
        void fileTemplateUtils.getByPageId.invalidate({
          projectId,
          pageId: currentFile.id,
        });
      }
      toast.success({
        title: "Page Updated",
        description: "Your page is ready to share",
      });
    },
    onError: () => {
      setIsPopoverOpen(false);
    },
  });

  const { mutate: updateTemplateName } =
    fileTemplateRouter.updateName.useMutation({
      onSuccess: () => {
        if (projectId && currentFile) {
          void fileTemplateUtils.getByPageId.invalidate({
            projectId,
            pageId: currentFile.id,
          });
        }
        if (!isSilentUpdateRef.current) {
          toast.success({
            title: "Page Updated",
            description: "Page name has been updated",
          });
        }
        isSilentUpdateRef.current = false;
      },
      onError: (error) => {
        if (!isSilentUpdateRef.current) {
          errorToast(
            "Failed to update template",
            "Please try again",
            "error.template.update",
            { error: String(error) },
          );
        }
        isSilentUpdateRef.current = false;
      },
    });

  const shareUrl =
    templateMutationData?.shareUrl ?? template?.shortLink ?? null;
  const templateUpdatedAt = template ? new Date(template.updatedAt) : null;
  const desktopPreviewUrl = template?.desktopPreviewUrl ?? null;
  const templateId = template?.id ?? null;

  const handleButtonClick = () => {
    if (shareUrl) {
      setIsPopoverOpen(true);
    } else if (currentFile && projectId) {
      createOrUpdateTemplate({
        projectId,
        fileId: currentFile.id,
        name: currentFile.name,
      });
    }
  };

  const handleRefresh = () => {
    if (templateId && templateName !== template?.name) {
      isSilentUpdateRef.current = false;
      updateTemplateName({
        id: templateId,
        name: templateName,
      });
    }
    if (currentFile && projectId) {
      createOrUpdateTemplate({
        projectId,
        fileId: currentFile.id,
        name: templateName,
      });
    }
  };

  const onBlurUpdateName = (data: TemplateNameFormData) => {
    if (templateId && data.name !== template?.name) {
      isSilentUpdateRef.current = true;
      updateTemplateName({
        id: templateId,
        name: data.name,
      });
    }
  };

  const onSubmitForm = (data: TemplateNameFormData) => {
    if (templateId && data.name !== template?.name) {
      isSilentUpdateRef.current = true;
      updateTemplateName({
        id: templateId,
        name: data.name,
      });
    }
  };

  const handleCopyLink = async () => {
    if (shareUrl) {
      const success = await writeHtmlToClipboard(shareUrl, errorToast);
      if (success) {
        setIsCopied(true);
        toast.success({
          title: "Link copied",
          description: "Share link copied to clipboard",
        });
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
    }
  };

  const handleOpenLink = () => {
    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  return (
    <PopoverRoot
      open={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        if (!open) {
          setIsCopied(false);
        }
      }}
    >
      <PopoverTrigger
        render={
          <Button variant="secondary" size="base" onClick={handleButtonClick} />
        }
      >
        Share
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        showCloseButton={false}
        UNSAFE_className="w-[360px]"
      >
        {(() => {
          if (isLoadingTemplate) {
            return <SharePopoverSkeleton />;
          }
          if (isCreatingTemplate && !template) {
            return <InitialCreationLoadingState templateName={templateName} />;
          }
          return (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div>
                  <h3 className="header-h5 text-default">Share Page</h3>
                  <p className="text-sm text-muted">
                    Anyone with the link can make their own copy!
                  </p>
                </div>

                <div>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void handleSubmit(onSubmitForm)(e);
                    }}
                    onBlur={() => void handleSubmit(onBlurUpdateName)()}
                  >
                    <LabeledControl
                      label="Page Name"
                      error={errors.name?.message}
                      size="base"
                    >
                      <Input
                        {...register("name")}
                        size="base"
                        placeholder="Enter template name"
                      />
                    </LabeledControl>
                  </form>
                </div>

                <AffiliateShareCard
                  shareUrl={shareUrl}
                  isCopied={isCopied}
                  onCopy={() => void handleCopyLink()}
                  onOpenLink={handleOpenLink}
                />
              </div>

              <div className="border-b-0.5 border-border -mx-3" />

              <div className="flex flex-col gap-2">
                <div className="text-sm">
                  <h4 className="font-medium text-default">Your Page</h4>
                  {templateUpdatedAt && (
                    <p className="text-muted">
                      Last updated{" "}
                      {formatDistanceToNowStrict(templateUpdatedAt)} ago
                    </p>
                  )}
                </div>

                <div className="group rounded-lg overflow-hidden relative">
                  {desktopPreviewUrl ? (
                    <img
                      src={desktopPreviewUrl}
                      alt={template?.name ?? "Template preview"}
                      className="w-full h-auto"
                    />
                  ) : (
                    <div className="aspect-video bg-subtle flex items-center justify-center text-muted" />
                  )}

                  <div
                    className={twMerge(
                      "absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-100",
                      isCreatingTemplate
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                  >
                    <Button
                      variant="secondary"
                      size="base"
                      onClick={handleRefresh}
                      loading={isCreatingTemplate}
                    >
                      Recapture
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </PopoverContent>
    </PopoverRoot>
  );
};
